import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import AtomDateRangePicker from '@/components/Atoms/AtomDateRangePicker/AtomDateRangePicker'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomVirtualizedTable from '@/components/Atoms/AtomVirtualizedTable/AtomVirtualizedTable'
import Loader from '@/components/Atoms/Loader/Loader'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { OrganisationInterface } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { selectedOrganisationCodeCryptographer } from '@/utils/auth/selectedOrganisationCodeCryptographer'

import {
  CLIENTS_COLUMN_WIDTHS,
  type ClientsSortDir,
  getColumns,
} from './ClientsTab.config'
import cn from './ClientsTab.module.scss'

const CLIENT_ROW_HEIGHT = 50

type ClientsTabProps = {
  sortDir: ClientsSortDir | null
  onToggleSort: () => void
}

function ClientsTab({ sortDir, onToggleSort }: ClientsTabProps) {
  const navigate = useNavigate()
  const [modal, contextHolder] = Modal.useModal()

  const {
    setSelectedOrganisation,
    setUserContext,
    setIsOrgSwitching,
    selectedOrganisation,
  } = useUserInfoStore((state) => state)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const { setProductInfoToCartPresence } = useFeedStore((state) => state)

  const [clients, setClients] = useState<OrganisationInterface[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState<dayjs.Dayjs | null>(null)
  const [dateTo, setDateTo] = useState<dayjs.Dayjs | null>(null)

  const debouncedSearch = useDebounce(search, 500)

  const lastPageRef = useRef(-1)
  const hasMoreRef = useRef(true)
  const isFetchingRef = useRef(false)

  const fetchClients = useCallback(
    async (isInitial = false) => {
      if (isFetchingRef.current || (!isInitial && !hasMoreRef.current)) return

      const pageToFetch = isInitial ? 0 : lastPageRef.current + 1
      isFetchingRef.current = true
      setLoading(true)

      try {
        const params: Record<string, string | number> = {
          page: pageToFetch,
          pageSize: 20,
        }

        if (debouncedSearch) {
          params.description = debouncedSearch
        }

        if (dateFrom) {
          params.registrationDateFrom = `${dateFrom.year()}-${dateFrom.month() + 1}-${dateFrom.date()} ${dateFrom.format('HH:mm:ss')}`
        }
        if (dateTo) {
          params.registrationDateTo = `${dateTo.year()}-${dateTo.month() + 1}-${dateTo.date()} ${dateTo.format('HH:mm:ss')}`
        }
        if (sortDir) {
          params.registrationDateOrderBy = sortDir.toUpperCase()
        }

        const response = await api.get('/organisations', { params })

        const data = response.data.organisations
        const content = data.content || []

        setClients((prev) => (isInitial ? content : [...prev, ...content]))
        setTotal(data.totalElements || 0)

        lastPageRef.current = pageToFetch
        hasMoreRef.current = pageToFetch < (data.totalPages ?? 1) - 1
        setHasNextPage(hasMoreRef.current)
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        setLoading(false)
        setInitialLoading(false)
        isFetchingRef.current = false
      }
    },
    [debouncedSearch, dateFrom, dateTo, sortDir, modal]
  )

  useEffect(() => {
    lastPageRef.current = -1
    hasMoreRef.current = true
    fetchClients(true)
  }, [debouncedSearch, dateFrom, dateTo, sortDir])

  const handleNameClick = async (client: OrganisationInterface) => {
    setIsOrgSwitching(true)
    const loadingToast = toast.loading('Переход к клиенту...')
    const prevClientId = localStorage.getItem('currentClientId')
    try {
      localStorage.setItem('currentClientId', client.id)
      localStorage.setItem(
        'selectedOrganisationCode',
        selectedOrganisationCodeCryptographer.encrypt(client.code)
      )

      const response = await api.get('/context', {
        params: { clientId: client.id },
      })
      const contextData = response.data.context

      setUserContext(contextData)
      setSelectedOrganisation(contextData.currentClient || {})
      setProductInfoToCartPresence({})

      toast.success('Переход в настройки клиента', {
        id: loadingToast,
        description: client.description,
      })
      navigate(`/organisations/${client.code}/settings`, {
        state: { prevClientId, fromClients: true },
      })
    } catch (error) {
      toast.dismiss(loadingToast)
      localStorage.removeItem('currentClientId')
      localStorage.removeItem('selectedOrganisationCode')
      handleApiError(error, modal)
    } finally {
      setIsOrgSwitching(false)
    }
  }

  const columns = getColumns({
    onNameClick: handleNameClick,
    sortDir,
    toggleSort: onToggleSort,
    isOrganisationSelected: isOrgSelected,
  })

  return (
    <div className={cn.clientsTab}>
      {contextHolder}
      <div className={cn.filtersBar}>
        <AtomSearchInput
          placeholder="Поиск по названию"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(value) => setSearch(value)}
          className={cn.filterInput}
          isOrganisationSelected={isOrgSelected}
        />
        <AtomDateRangePicker
          value={{ from: dateFrom, to: dateTo }}
          onChange={({ from, to }) => {
            setDateFrom(from)
            setDateTo(to)
          }}
          isOrganisationSelected={isOrgSelected}
        />
        <span
          className={classNames(
            cn.foundCount,
            isOrgSelected && cn.foundCountSelected
          )}
        >
          Найдено: {initialLoading ? '-' : total}
        </span>
      </div>

      {initialLoading ? (
        <Loader size={32} />
      ) : (
        <>
          <AtomVirtualizedTable
            ariaLabel="Клиенты"
            data={clients}
            columns={columns}
            getRowId={(client) => client.id}
            columnWidths={CLIENTS_COLUMN_WIDTHS}
            estimateRowHeight={CLIENT_ROW_HEIGHT}
            isOrganisationSelected={isOrgSelected}
            hasFloatingHeader
            infiniteScroll={{
              hasNextPage,
              isFetchingNextPage: loading,
              onLoadMore: () => fetchClients(false),
            }}
          />
          {!hasNextPage && clients.length > 0 && (
            <div className={cn.endOfList}>Все клиенты загружены</div>
          )}
        </>
      )}
    </div>
  )
}

export default ClientsTab
